import { baseApi } from './baseApi';
import { normalizeKeys } from '../../utils/transform';

export interface Organization {
  organizationId: string;
  name: string;
  logo?: string;
  stats: {
    totalAgents: number;
    totalSessions: number;
    activeAgents: number;
  };
  createdAt: string;
}

export interface Member {
  userId: string;
  email: string;
  name?: string;
  role: 'owner' | 'admin' | 'member' | 'viewer';
  status: 'active' | 'invited';
  lastActive?: string;
  createdAt: string;
}

export const organizationApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getOrganization: builder.query<Organization, string>({
      query: (orgId) => `/organizations/${orgId}`,
      transformResponse: (raw: unknown) => normalizeKeys<Organization>(raw),
      providesTags: ['Organization'],
    }),
    getMembers: builder.query<{ members: Member[]; total: number }, string>({
      query: (orgId) => `/organizations/${orgId}/members`,
      transformResponse: (raw: unknown) =>
        normalizeKeys<{ members: Member[]; total: number }>(raw),
      providesTags: ['Organization'],
    }),
    inviteMember: builder.mutation<
      Member,
      { orgId: string; email: string; role: string }
    >({
      query: ({ orgId, email, role }) => ({
        url: `/organizations/${orgId}/members/invite`,
        method: 'POST',
        body: { email, role },
      }),
      transformResponse: (raw: unknown) => normalizeKeys<Member>(raw),
      invalidatesTags: ['Organization'],
    }),
    updateMemberRole: builder.mutation<
      Member,
      { orgId: string; userId: string; role: string }
    >({
      query: ({ orgId, userId, role }) => ({
        url: `/organizations/${orgId}/members/${userId}`,
        method: 'PUT',
        body: { role },
      }),
      transformResponse: (raw: unknown) => normalizeKeys<Member>(raw),
      invalidatesTags: ['Organization'],
    }),
    removeMember: builder.mutation<
      { userId: string; deleted: boolean },
      { orgId: string; userId: string }
    >({
      query: ({ orgId, userId }) => ({
        url: `/organizations/${orgId}/members/${userId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Organization'],
    }),
  }),
});

export const {
  useGetOrganizationQuery,
  useGetMembersQuery,
  useInviteMemberMutation,
  useUpdateMemberRoleMutation,
  useRemoveMemberMutation,
} = organizationApi;
